import React, { useEffect, useState } from "react";
import { useLocation, Navigate, useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { useProfile, useRedux } from "../../hooks";
import { createSelector } from "reselect";
import { loginUser } from "../../redux/actions";
import { userForgetPassword } from "../../redux/auth/forgetpwd/actions";
import LoginLayoutWrapper from "../../components/LoginLayoutWrapper";
import AccountCreationForm, {
  AccountCreationFormValues,
} from "../../components/AccountCreationForm";
import RecoverPasswordModal from "../../components/RecoverPasswordModal";
import { CookieConsent } from "../../components/CookieConsent";
import { signUp } from "../../api/auth";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";

import {
  Box,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  CircularProgress,
  Alert,
  CssBaseline,
  Divider,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import logger from "../../utils/logger";
import api from "../../utils/api";
import Cookies from "js-cookie";
import logoJesss from "../../assets/logos/Logo_jesss.svg";

type LoginInputs = { email: string; password?: string };

const createLoginSchema = (isRecovery: boolean) =>
  yup.object({
    email: yup
      .string()
      .email("login.emailInvalid")
      .required("login.emailRequired"),
    password: isRecovery
      ? yup.string().optional()
      : yup.string().required("login.passwordRequired"),
  });

const fieldAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, loading: profileLoading } = useProfile();
  const { dispatch, useAppSelector } = useRedux();

  const {
    isUserLogin,
    error,
    loginLoading,
    isUserLogout,
    desktopAuthSuccess: reduxDesktopAuthSuccess,
  } = useAppSelector(
    createSelector(
      (s: any) => s.Login,
      (st) => ({
        isUserLogin: st.isUserLogin,
        error: st.error,
        loginLoading: st.loading,
        isUserLogout: st.isUserLogout,
        desktopAuthSuccess: st.user?.desktopAuthSuccess,
      }),
    ),
  );

  const { forgetPasswordLoading, forgetPasswordError, forgetPasswordSuccess } =
    useAppSelector(
      createSelector(
        (s: any) => s.ForgetPassword,
        (st) => ({
          forgetPasswordLoading: st.loading,
          forgetPasswordError: st.error,
          forgetPasswordSuccess: st.emailSent,
        }),
      ),
    );

  const [mode, setMode] = useState<"login" | "signup">("login");
  const [redirectUrl, setRedirectUrl] = useState("/");
  const [showPassword, setShowPassword] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const [codeChallenge, setCodeChallenge] = useState<string | null>(null);
  const [redirectUri, setRedirectUri] = useState<string | null>(null);
  const [state, setState] = useState<string | null>(null);
  const [port, setPort] = useState<string | null>(null);
  const [showDesktopSuccess, setShowDesktopSuccess] = useState(false);
  const [googleAuthEnabled, setGoogleAuthEnabled] = useState(false);

  useEffect(() => {
    api
      .get<Record<string, boolean>>("/api/public/platform-flags")
      .then((r) => {
        if (typeof r.data?.google_auth === "boolean") {
          setGoogleAuthEnabled(r.data.google_auth);
        }
      })
      .catch(() => {
        // falha silenciosa — mantém false (oculto)
      });
  }, []);

  const handleGoogleLogin = () => {
    const isDesktopFlow =
      codeChallenge &&
      redirectUri &&
      state &&
      port &&
      codeChallenge !== "" &&
      redirectUri !== "" &&
      state !== "" &&
      port !== "";

    if (isDesktopFlow) {
      Cookies.set("code_challenge", codeChallenge);
      Cookies.set("redirect_uri", redirectUri);
      Cookies.set("state", state);
      Cookies.set("port", port);
    }

    const googleLoginUrl = "/api/auth/google/login";
    window.location.href = googleLoginUrl;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<LoginInputs>({
    resolver: yupResolver(createLoginSchema(isPasswordRecovery)),
    mode: "onSubmit",
  });

  const watchedValues = watch();

  useEffect(() => {
    if (mode !== "login" || isPasswordRecovery) {
      return;
    }

    const checkAutofillValues = () => {
      if (mode !== "login" || isPasswordRecovery) {
        return;
      }
      const loginForm = document.querySelector(
        'form[data-testid="login-form"], form:has(input[name="email"]):has(input[name="password"])',
      );
      if (!loginForm) {
        return;
      }

      const emailInput = document.querySelector(
        'input[name="email"]',
      ) as HTMLInputElement;
      const passwordInput = document.querySelector(
        'input[name="password"]',
      ) as HTMLInputElement;

      if (emailInput && loginForm.contains(emailInput) && emailInput.value) {
        const currentValue = watchedValues.email || "";
        if (emailInput.value !== currentValue) {
          setValue("email", emailInput.value, {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
      }

      if (
        passwordInput &&
        loginForm.contains(passwordInput) &&
        passwordInput.value
      ) {
        const currentValue = watchedValues.password || "";
        if (passwordInput.value !== currentValue) {
          setValue("password", passwordInput.value, {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (mode === "login" && !isPasswordRecovery && e.key === "Enter") {
        const activeElement = document.activeElement;
        if (
          activeElement &&
          (activeElement.tagName === "BUTTON" ||
            activeElement.getAttribute("role") === "button" ||
            activeElement.closest("button") ||
            activeElement.closest('[role="button"]'))
        ) {
          return;
        }
        setTimeout(checkAutofillValues, 10);
      }
    };

    const handleInputChange = () => {
      if (mode === "login" && !isPasswordRecovery) {
        setTimeout(checkAutofillValues, 50);
      }
    };

    const timeoutId = setTimeout(checkAutofillValues, 100);
    const intervalId = setInterval(checkAutofillValues, 300);

    document.addEventListener("input", handleInputChange);
    document.addEventListener("change", handleInputChange);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      document.removeEventListener("input", handleInputChange);
      document.removeEventListener("change", handleInputChange);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    setValue,
    watchedValues.email,
    watchedValues.password,
    mode,
    isPasswordRecovery,
  ]);

  useEffect(() => {
    setRedirectUrl((location.state as any)?.from?.pathname || "/");
  }, [location]);

  useEffect(() => {
    reset();
    if (mode === "signup") {
      setValue("email", "", { shouldValidate: false });
      setValue("password", "", { shouldValidate: false });

      setTimeout(() => {
        const emailInput = document.querySelector(
          'input[name="email"]',
        ) as HTMLInputElement;
        const passwordInput = document.querySelector(
          'input[name="password"]',
        ) as HTMLInputElement;

        if (
          emailInput &&
          emailInput.form?.getAttribute("data-testid") === "login-form"
        ) {
          emailInput.value = "";
        }
        if (
          passwordInput &&
          passwordInput.form?.getAttribute("data-testid") === "login-form"
        ) {
          passwordInput.value = "";
        }
      }, 50);
    }
  }, [mode, isPasswordRecovery, reset, setValue]);

  useEffect(() => {
    if (
      isUserLogin &&
      !loginLoading &&
      !isUserLogout &&
      !reduxDesktopAuthSuccess
    ) {
      const authUser = localStorage.getItem("authUser");

      if (authUser) {
        try {
          const userData = JSON.parse(authUser);
          const companyId = userData.companyId || userData.companyID;

          if (
            !companyId ||
            companyId === 0 ||
            companyId === "0" ||
            companyId === ""
          ) {
            navigate("/onboarding");
            return;
          }
        } catch (e) {}
      }
      navigate(redirectUrl);
    }
  }, [
    isUserLogin,
    loginLoading,
    isUserLogout,
    navigate,
    redirectUrl,
    reduxDesktopAuthSuccess,
  ]);

  useEffect(() => {
    if (forgetPasswordSuccess) {
      setTimeout(() => {
        setIsPasswordRecovery(false);
      }, 3000);
    }
  }, [forgetPasswordSuccess]);

  useEffect(() => {
    if (reduxDesktopAuthSuccess) {
      setShowDesktopSuccess(true);
    }
  }, [reduxDesktopAuthSuccess]);

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const challenge = urlParams.get("code_challenge");
    const uri = urlParams.get("redirect_uri");
    const stateParam = urlParams.get("state");
    const urlPort = urlParams.get("port");
    const error = urlParams.get("error");

    if (challenge) {
      setCodeChallenge(challenge);
    }

    if (uri) {
      setRedirectUri(uri);
    }

    if (stateParam) {
      setState(stateParam);
    }

    if (urlPort) {
      setPort(urlPort);
    }

    if (error) {
      toast.error(t("login.google_login_error"));
    }
  }, [t]);

  useEffect(() => {
    const emailInput = document.querySelector(
      'input[name="email"]',
    ) as HTMLInputElement;

    emailInput?.focus();
  }, []);

  if (userProfile && !profileLoading && !redirectUri && !codeChallenge) {
    // Check if user has completed company setup
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        const userData = JSON.parse(authUser);
        // Check both companyId and companyID (API might return uppercase)
        const companyId = userData.companyId || userData.companyID;
        // If companyId is missing, empty, empty string, or zero, redirect to onboarding
        if (
          !companyId ||
          companyId === 0 ||
          companyId === "0" ||
          companyId === ""
        ) {
          return <Navigate to="/onboarding" />;
        }
      } catch (e) {
        logger.error("Error parsing auth user data:", e);
      }
    }
    return <Navigate to={redirectUrl} />;
  }

  // login
  const onSubmitLogin: SubmitHandler<LoginInputs> = (data) => {
    if (mode !== "login" || isPasswordRecovery) {
      return;
    }

    dispatch(
      loginUser({
        email: data.email.trim().toLowerCase(),
        password: data.password,
        ...(codeChallenge && { code_challenge: codeChallenge }),
        ...(redirectUri && { redirect_uri: redirectUri }),
        ...(state && { state: state }),
        ...(port && { port: port }),
      }),
    );
  };

  const onSubmitPasswordRecovery: SubmitHandler<LoginInputs> = (data) => {
    dispatch(userForgetPassword(data.email.trim().toLowerCase()));
  };
  const onEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const cleanedEmail = e.target.value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^\w@.-]/g, "");

    setValue("email", cleanedEmail, {
      shouldValidate: true,
    });
  };

  // to clear and validate password
  const onPasswordBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const cleanedPassword = e.target.value.trim().replace(/\s+/g, "");

    setValue("password", cleanedPassword, {
      shouldValidate: true,
    });
  };

  // to prevent real-time spaces in email
  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanValue = e.target.value.replace(/^\s+/, "");
    e.target.value = cleanValue;
  };

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleanValue = e.target.value.replace(/^\s+/, "");
    e.target.value = cleanValue;
  };

  // signup
  const handleSignUp: SubmitHandler<AccountCreationFormValues> = async (
    data,
  ) => {
    setSignUpError(null);
    setSignUpLoading(true);
    try {
      const response = await signUp({
        name: data.name,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        termsAccepted: data.termsAccepted ?? false,
        auth_provider: "system",
      });

      const userData = response.data?.data || response.data;
      if (userData) localStorage.setItem("authUser", JSON.stringify(userData));

      navigate("/onboarding");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.response?.data?.message;

      // Handle specific error messages and translate them
      if (errorMessage?.includes("email already registered")) {
        setSignUpError(t("login.emailAlreadyRegistered"));
      } else if (
        errorMessage?.includes("password must be at least 8 characters long")
      ) {
        setSignUpError(t("login.passwordMustBe8Chars"));
      } else {
        setSignUpError(errorMessage || t("login.signupError"));
        logger.error(err);
      }
    } finally {
      setSignUpLoading(false);
    }
  };

  if (userProfile && !profileLoading && !redirectUri && !codeChallenge) {
    return <Navigate to={{ pathname: redirectUrl }} />;
  }

  const layoutMode = isPasswordRecovery ? "passwordRecovery" : mode;

  return (
    <LoginLayoutWrapper mode={layoutMode}>
      <CssBaseline />
      <CookieConsent />

      {mode === "login" && !isPasswordRecovery && error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(220, 38, 38, 0.15)",
            "& .MuiAlert-message": {
              fontSize: "0.95rem",
            },
          }}
        >
          {t(error)}
        </Alert>
      )}
      {mode === "login" && isPasswordRecovery && forgetPasswordError && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(220, 38, 38, 0.15)",
            "& .MuiAlert-message": {
              fontSize: "0.95rem",
            },
          }}
        >
          {forgetPasswordError}
        </Alert>
      )}
      {mode === "login" && isPasswordRecovery && forgetPasswordSuccess && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(34, 197, 94, 0.15)",
            "& .MuiAlert-message": {
              fontSize: "0.95rem",
            },
          }}
        >
          {t("login.passwordRecoveryEmailSent")}
        </Alert>
      )}
      {mode === "signup" && signUpError && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(220, 38, 38, 0.15)",
            "& .MuiAlert-message": {
              fontSize: "0.95rem",
            },
          }}
        >
          {signUpError}
        </Alert>
      )}
      {showDesktopSuccess && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(34, 197, 94, 0.15)",
            "& .MuiAlert-message": {
              fontSize: "0.95rem",
            },
          }}
        >
          {t("login.desktopAuthSuccess")}
        </Alert>
      )}

      {/* Logo Icon - Mobile and Tablet */}
      <Box
        sx={{
          display: { xs: "flex", lg: "none" },
          justifyContent: "center",
          alignItems: "center",
          mb: { xs: 4, sm: 4.5, md: 4 },
          width: "100%",
          "@media (max-width: 360px)": {
            mb: 3.5,
          },
          "@media (max-height: 450px) and (orientation: landscape)": {
            mb: 1,
          },
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <Box
            component="img"
            src={logoJesss}
            alt="Jesss"
            sx={{
              width: { xs: "70px", sm: "80px", md: "100px" },
              height: { xs: "70px", sm: "80px", md: "100px" },
              objectFit: "contain",
              "@media (max-width: 360px)": {
                width: "64px",
                height: "64px",
              },
              "@media (max-height: 450px) and (orientation: landscape)": {
                width: "48px",
                height: "48px",
              },
            }}
          />
        </motion.div>
      </Box>

      {/* Header - Título */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: { xs: 5, sm: 5.5, md: 5, lg: 4 },
          width: "100%",
          "@media (max-width: 360px)": {
            mb: 4.5,
          },
          "@media (max-height: 450px) and (orientation: landscape)": {
            mb: 1,
          },
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={mode + "-title"}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            style={{ width: "100%" }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "700",
                color: "#54398E",
                fontSize: {
                  xs: "1.7rem",
                  sm: "1.8rem",
                  md: "2.2rem",
                  lg: "2.5rem",
                },
                textAlign: "center",
                width: "100%",
                lineHeight: 1.1,
                "@media (max-width: 360px)": {
                  fontSize: "1.6rem",
                },
                "@media (max-height: 450px) and (orientation: landscape)": {
                  fontSize: "1.3rem",
                  mb: 0.5,
                },
              }}
            >
              {isPasswordRecovery
                ? t("login.recoverPassword")
                : mode === "login"
                  ? t("login.title")
                  : t("login.createAccount")}
            </Typography>
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Form */}
      <AnimatePresence mode="wait">
        {!showDesktopSuccess &&
          (mode === "login" && !isPasswordRecovery ? (
            <motion.form
              key="login-form"
              data-testid="login-form"
              onSubmit={handleSubmit(onSubmitLogin)}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {/* Email */}
              <motion.div {...fieldAnim} transition={{ delay: 0.1 }}>
                <TextField
                  fullWidth
                  size="medium"
                  label={t("login.email")}
                  variant="outlined"
                  margin="normal"
                  {...register("email")}
                  onChange={onEmailChange}
                  onBlur={onEmailBlur}
                  error={!!errors.email}
                  helperText={errors.email && t(errors.email.message!)}
                  inputProps={{
                    autoComplete: "email",
                  }}
                  sx={{
                    mb: { xs: 3.5, sm: 3.5, md: 3, lg: 2 },
                    "@media (max-width: 360px)": {
                      mb: 3,
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      color: "#000000",
                      height: { xs: "56px", md: "60px", lg: "56px" },
                      "& fieldset": {
                        borderColor: "#595959",
                      },
                      "&:hover fieldset": {
                        borderColor: "#BDBDBD",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#54398E",
                        borderWidth: "2px",
                      },
                      "@media (max-width: 360px)": {
                        height: "52px",
                      },
                      "@media (max-height: 450px) and (orientation: landscape)":
                        {
                          height: "44px",
                        },
                    },
                    "@media (max-height: 450px) and (orientation: landscape)": {
                      mb: 1,
                    },
                    "& .MuiInputLabel-root": {
                      color: "#595959",
                      fontSize: "14px",
                      backgroundColor: "#FAFAFA",
                      px: 1,
                      "&.Mui-focused": {
                        color: "#595959",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#000000",
                      fontSize: "16px",
                      "&::placeholder": {
                        color: "#595959",
                        opacity: 1,
                      },
                    },
                  }}
                />
              </motion.div>

              {/* Senha */}
              <motion.div {...fieldAnim} transition={{ delay: 0.3 }}>
                <TextField
                  fullWidth
                  size="medium"
                  type={showPassword ? "text" : "password"}
                  label={t("login.password")}
                  variant="outlined"
                  margin="normal"
                  {...register("password")}
                  onChange={onPasswordChange}
                  onBlur={onPasswordBlur}
                  error={!!errors.password}
                  helperText={errors.password && t(errors.password.message!)}
                  inputProps={{
                    autoComplete: "current-password",
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((p) => !p)}
                          size="small"
                          sx={{ color: "#595959" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: { xs: 1.5, sm: 1.5, md: 1.5, lg: 0.5 },
                    "@media (max-width: 360px)": {
                      mb: 1,
                    },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      color: "#000000",
                      height: { xs: "56px", md: "60px", lg: "56px" },
                      "& fieldset": {
                        borderColor: "#BDBDBD",
                      },
                      "&:hover fieldset": {
                        borderColor: "#BDBDBD",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#54398E",
                        borderWidth: "2px",
                      },
                      "@media (max-width: 360px)": {
                        height: "52px",
                      },
                      "@media (max-height: 450px) and (orientation: landscape)":
                        {
                          height: "44px",
                        },
                    },
                    "@media (max-height: 450px) and (orientation: landscape)": {
                      mb: 0.5,
                    },
                    "& .MuiInputLabel-root": {
                      color: "#595959",
                      fontSize: "14px",
                      backgroundColor: "#FAFAFA",
                      px: 1,
                      "&.Mui-focused": {
                        color: "#595959",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#000000",
                      fontSize: "16px",
                      "&::placeholder": {
                        color: "#595959",
                        opacity: 1,
                      },
                    },
                  }}
                />
              </motion.div>

              {/* Esqueci a senha */}
              <motion.div {...fieldAnim} transition={{ delay: 0.35 }}>
                <Box
                  sx={{
                    textAlign: "right",
                    mt: { xs: 3, sm: 3, md: 2.5, lg: 1.5 },
                    mb: { xs: 5, sm: 5, md: 4, lg: 3 },
                    "@media (max-width: 360px)": {
                      mt: 2.5,
                      mb: 4.5,
                    },
                    "@media (max-height: 450px) and (orientation: landscape)": {
                      mt: 0.5,
                      mb: 1,
                    },
                  }}
                >
                  <Typography
                    component="span"
                    onClick={() => setIsPasswordRecovery(true)}
                    sx={{
                      color: "#54398E",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      textDecoration: "none",
                      "&:hover": {
                        color: "#3d2868",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {t("login.forgotPassword")}
                  </Typography>
                </Box>
              </motion.div>

              {/* Botão Login */}
              <motion.div {...fieldAnim} transition={{ delay: 0.4 }}>
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  type="submit"
                  disabled={loginLoading}
                  sx={{
                    backgroundColor: "#54398E",
                    color: "#FFF9F9",
                    fontWeight: "600",
                    fontSize: "1.25rem",
                    py: 1.8,
                    px: 3,
                    borderRadius: "8px",
                    textTransform: "none",
                    boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.05)",
                    mb: { xs: 4.5, sm: 4.5, md: 4, lg: 3.5 },
                    mt: 0,
                    height: { xs: "56px", md: "60px", lg: "56px" },
                    "@media (max-width: 360px)": {
                      mb: 4,
                      height: "52px",
                      fontSize: "1.1rem",
                    },
                    "&:hover": {
                      backgroundColor: "#3d2868",
                      boxShadow: "0px 6px 12px 0px rgba(0, 0, 0, 0.1)",
                    },
                    "&:disabled": {
                      backgroundColor: "#a0aec0",
                      color: "#ffffff",
                      boxShadow: "0px 2px 4px 0px rgba(0, 0, 0, 0.05)",
                    },
                    transition: "all 0.2s ease",
                    "@media (max-height: 450px) and (orientation: landscape)": {
                      height: "44px",
                      py: 1,
                      mb: 1.5,
                      fontSize: "1rem",
                    },
                  }}
                >
                  {loginLoading ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    t("loginButton")
                  )}
                </Button>
              </motion.div>

              {/* Divider OU - apenas se Google estiver habilitado */}
              {googleAuthEnabled && (
                <motion.div {...fieldAnim} transition={{ delay: 0.45 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      my: { xs: 3, md: 8 },
                      position: "relative",
                    }}
                  >
                    <Divider sx={{ flexGrow: 1, borderColor: "#E0E0E0" }} />
                    <Box
                      sx={{
                        position: "absolute",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#ffffff",
                        px: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#595959",
                          fontSize: "0.8rem",
                          fontWeight: "700",
                          textAlign: "center",
                        }}
                      >
                        {t("login.or")}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              )}

              {/* Login com Google - apenas se estiver habilitado */}
              {googleAuthEnabled && (
                <motion.div {...fieldAnim} transition={{ delay: 0.5 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={handleGoogleLogin}
                    startIcon={
                      <Box
                        component="img"
                        src="https://developers.google.com/identity/images/g-logo.png"
                        alt="Google"
                        sx={{ width: 20, height: 20 }}
                      />
                    }
                    sx={{
                      py: 1.5,
                      px: 3,
                      borderRadius: "8px",
                      borderColor: "#755FA3",
                      backgroundColor: "transparent",
                      color: "#755FA3",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      textTransform: "none",
                      boxShadow: "0px 4px 8px 0px rgba(0, 0, 0, 0.05)",
                      height: "49px",
                      mb: { xs: 3.5, md: 7 },
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#54398E",
                        backgroundColor: "rgba(117, 95, 163, 0.04)",
                        color: "#54398E",
                        boxShadow: "0px 6px 12px 0px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    {t("login.signInWithGoogle")}
                  </Button>
                </motion.div>
              )}

              {/* SWITCH */}
              <motion.div {...fieldAnim} transition={{ delay: 0.55 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 0.5,
                    mt: {
                      xs: googleAuthEnabled ? 0 : 4,
                      sm: googleAuthEnabled ? 0 : 4,
                      md: googleAuthEnabled ? 0 : 4.5,
                      lg: googleAuthEnabled ? 0 : 4,
                    },
                    "@media (max-width: 360px)": {
                      mt: googleAuthEnabled ? 0 : 3.5,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: "#595959",
                      fontSize: "0.875rem",
                      fontWeight: "400",
                    }}
                  >
                    {t("login.noAccount")}
                  </Typography>
                  <Link
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const form = (e.target as HTMLElement).closest("form");
                      if (form) {
                        form.onsubmit = (event) => {
                          event.preventDefault();
                          return false;
                        };
                      }

                      setValue("email", "", { shouldValidate: false });
                      setValue("password", "", { shouldValidate: false });

                      setMode("signup");

                      setTimeout(() => {
                        if (form) {
                          form.onsubmit = null;
                        }
                      }, 100);
                    }}
                    sx={{
                      fontWeight: "500",
                      fontSize: "0.875rem",
                      color: "#54398E",
                      textDecoration: "none",
                      "&:hover": {
                        color: "#3d2868",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {t("login.createAccount")}
                  </Link>
                </Box>
              </motion.div>
            </motion.form>
          ) : mode === "login" && isPasswordRecovery ? (
            <motion.form
              key="password-recovery-form"
              onSubmit={handleSubmit(onSubmitPasswordRecovery)}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              {/* Email */}
              <motion.div {...fieldAnim} transition={{ delay: 0.1 }}>
                <TextField
                  fullWidth
                  size="medium"
                  label={t("login.email")}
                  variant="outlined"
                  margin="normal"
                  {...register("email")}
                  onChange={onEmailChange}
                  onBlur={onEmailBlur}
                  error={!!errors.email}
                  helperText={errors.email && t(errors.email.message!)}
                  sx={{
                    mb: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      color: "#000000",
                      height: "56px",
                      "& fieldset": {
                        borderColor: "#595959",
                      },
                      "&:hover fieldset": {
                        borderColor: "#BDBDBD",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#54398E",
                        borderWidth: "2px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#595959",
                      fontSize: "14px",
                      backgroundColor: "#FAFAFA",
                      px: 1,
                      "&.Mui-focused": {
                        color: "#595959",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#000000",
                      fontSize: "16px",
                      "&::placeholder": {
                        color: "#595959",
                        opacity: 1,
                      },
                    },
                  }}
                />
              </motion.div>

              {/* Descrição para recuperação de senha */}
              <motion.div {...fieldAnim} transition={{ delay: 0.2 }}>
                <Box sx={{ mt: 2, mb: 3 }}>
                  <Typography
                    sx={{
                      color: "#4a5568",
                      fontSize: "0.95rem",
                      lineHeight: 1.5,
                      textAlign: "center",
                      backgroundColor: "#f7fafc",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    {t("login.recoverPasswordDescription")}
                  </Typography>
                </Box>
              </motion.div>

              {/* Botão de Voltar para o Login */}
              <motion.div {...fieldAnim} transition={{ delay: 0.3 }}>
                <Box sx={{ textAlign: "center", mt: 2, mb: 2 }}>
                  <Typography
                    component="span"
                    onClick={() => setIsPasswordRecovery(false)}
                    sx={{
                      color: "#54398E",
                      fontWeight: "600",
                      fontSize: "0.9rem",
                      cursor: "pointer",
                      textDecoration: "none",
                      "&:hover": {
                        color: "#3d2868",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {t("login.backToLogin")}
                  </Typography>
                </Box>
              </motion.div>

              {/* Botão Enviar */}
              <motion.div {...fieldAnim} transition={{ delay: 0.4 }}>
                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  type="submit"
                  disabled={forgetPasswordLoading}
                  sx={{
                    background:
                      "linear-gradient(135deg, #54398E 0%, #3d2868 100%)",
                    color: "white",
                    fontWeight: "700",
                    fontSize: "1.1rem",
                    py: 2.5,
                    px: 8,
                    borderRadius: "12px",
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(84, 57, 142, 0.3)",
                    mb: 2,
                    mt: 3,
                    position: "relative",
                    overflow: "hidden",
                    border: "none",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                    },
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #3d2868 0%, #54398E 100%)",
                      boxShadow: "0 12px 32px rgba(84, 57, 142, 0.4)",
                      transform: "translateY(-2px)",
                      "&::before": {
                        opacity: 1,
                      },
                    },
                    "&:active": {
                      transform: "translateY(0px)",
                      boxShadow: "0 4px 16px rgba(84, 57, 142, 0.3)",
                    },
                    "&:disabled": {
                      background:
                        "linear-gradient(135deg, #a0aec0 0%, #718096 100%)",
                      boxShadow: "0 4px 15px rgba(160, 174, 192, 0.3)",
                      transform: "none",
                      "&::before": {
                        opacity: 0,
                      },
                    },
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  {forgetPasswordLoading ? (
                    <CircularProgress size={24} sx={{ color: "white" }} />
                  ) : (
                    t("login.sendButton")
                  )}
                </Button>
              </motion.div>
            </motion.form>
          ) : mode === "signup" ? (
            <motion.div
              key="signup-form"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <AccountCreationForm
                switchToLogin={() => setMode("login")}
                onSubmit={handleSignUp}
                loading={signUpLoading}
                googleAuthEnabled={googleAuthEnabled}
              />
            </motion.div>
          ) : null)}
      </AnimatePresence>

      {openForgotPassword && (
        <RecoverPasswordModal
          open
          onClose={() => setOpenForgotPassword(false)}
        />
      )}

      <ToastContainer />
    </LoginLayoutWrapper>
  );
};

export default Login;
